import React, { useState, useEffect } from 'react'
import DatamapsIndia from 'react-datamaps-india'
import {
  MapContainer,
  MapTitle,
  CustomLegend,
  LegendTitle,
  LegendItem,
  LegendColor,
  HoverInfo,
  StyledViewAllBtn,
} from './style'
import './style.css'
import ExpendArrowIco from 'components/icons/ExpendArrowIco'
import styled from 'styled-components'
// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  overflow-y: scroll;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const ModalContent = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  width: 80%;
  max-width: 800px;
  min-height: 90vh;
  height: auto;
  overflow-y: auto;
  position: relative;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${props => props.fontSize || '16px'};
  font-weight: 600;
  font-family: Plus Jakarta Sans;
  color: #000;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  &:hover {
    color: #000;
  }
`

const DetailedLegend = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: white;
  border-radius: 4px;
  padding: 10px;
  max-width: 180px;
  max-height: 450px;
  overflow-y: auto;
  z-index: 100;
  font-family: Plus Jakarta Sans;
  font-size: 12px;
  // border: 1px solid #eaeaea;
  // box-shadow: 0 2px 8px rgba(0,0,0,0.1);
`

const LegendRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  gap: 12px;
`

const ColorIndicator = styled.div`
  width: 18px;
  height: 12px;
  background-color: ${props => props.color};
  margin-right: 8px;
`

const StateCount = styled.div`
  font-weight: 600;
  display: flex;
  align-items: center;
`

const LegendScale = styled.div`
  width: 100%;
  height: 20px;
  background: linear-gradient(
    to right,
    #ffffff,
    #ffb84a,
    #f2891f,
    #de4424,
    #9c0a0d,
    #531011
  );
  margin-top: 10px;
  border-radius: 2px;
`

const Map = ({ mapMetrics }) => {
  const [showModal, setShowModal] = useState(false)
  // Custom function to determine color based on value
  const getColor = value => {
    if (value === 0) return '#CCCCCC'
    if (value >= 1 && value <= 5) return '#FFB84A'
    if (value >= 6 && value <= 10) return '#F2891F'
    if (value >= 11 && value <= 15) return '#DE4424'
    if (value >= 16 && value <= 20) return '#9C0A0D'
    if (value > 20) return '#531011'
    return '#CCCCCC' // Default color
  }

  // List of all states you want to show on the map
  const allStates = [
    'Jammu & Kashmir',
    'Himachal Pradesh',
    'Punjab',
    'Uttarakhand',
    'Haryana',
    'Karnataka',
    'Maharashtra',
    'Rajasthan',
    'Tamil Nadu',
    'Kerala',
    'Andaman & Nicobar Island',
    'Arunanchal Pradesh',
    'Assam',
    'Bihar',
    'Chandigarh',
    'Chhattisgarh',
    'Delhi',
    'Goa',
    'Jharkhand',
    'Madhya Pradesh',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Sikkim',
    'Telangana',
    'Tripura',
    'West Bengal',
    'Gujarat',
    'Uttar Pradesh',
    'Andhra Pradesh',
  ]

  // Map of canonical state names to possible alternate names
  const stateNameMap = {
    'Jammu & Kashmir': [
      'Jammu & Kashmir',
      'Jammu AND Kashmir',
      'Jammu and Kashmir',
    ],
    Odisha: ['Odisha', 'Orissa'],
    'Andaman & Nicobar Island': [
      'Andaman & Nicobar Island',
      'Andaman and Nicobar Islands',
      'Andaman & Nicobar Islands',
    ],
    Delhi: ['Delhi', 'NCT of Delhi'],
    // Add more alternates as needed
  }

  // Build a lowercase-keyed version of mapMetrics for case-insensitive lookup
  const mapMetricsLower = {}
  if (mapMetrics) {
    Object.keys(mapMetrics).forEach(key => {
      mapMetricsLower[key.toLowerCase()] = key
    })
  }

  const regionData = {}
  allStates.forEach(state => {
    let value = 0
    const alternates = stateNameMap[state] || [state]
    for (const alt of alternates) {
      const altLower = alt.toLowerCase()
      const originalKey = mapMetricsLower[altLower]
      if (originalKey !== undefined && mapMetrics[originalKey] !== undefined) {
        value = mapMetrics[originalKey]
        break
      }
    }
    regionData[state] = { value }
  })

  // Enhanced region data with color property
  const enhancedRegionData = {}
  Object.keys(regionData).forEach(state => {
    enhancedRegionData[state] = {
      ...regionData[state],
      color: getColor(regionData[state].value),
    }
  })

  // Add custom CSS to override positioning
  useEffect(() => {
    const style = document.createElement('style')
    style.innerHTML = `
      svg[preserveAspectRatio="xMinYMin meet"] {
        position: static !important;
        height: ${showModal ? '500px' : '300px'} !important;
        width: 100% !important;
      }
    `
    document.head.appendChild(style)

    return () => {
      document.head.removeChild(style)
    }
  }, [showModal])

  // Custom legend data matching our color scheme
  const legendData = [
    { range: 'NA', color: '#CCCCCC' },
    { range: '1-5', color: '#FFB84A' },
    { range: '6-10', color: '#F2891F' },
    { range: '11-15', color: '#DE4424' },
    { range: '16-20', color: '#9C0A0D' },
    { range: '>20', color: '#531011' },
  ]

  return (
    <>
      <MapContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MapTitle>
            Heat Map - <span>No. of Colleges by State</span>
          </MapTitle>
          <StyledViewAllBtn
            text="View"
            icon={<ExpendArrowIco />}
            height={'32px'}
            onClick={() => setShowModal(true)}
          />
        </div>

        {/* Custom Legend */}
        <CustomLegend>
          <LegendTitle>Colleges</LegendTitle>
          {legendData.map((item, index) => (
            <LegendItem key={index}>
              <span>{item.range}</span>
              <LegendColor color={item.color} />
            </LegendItem>
          ))}
        </CustomLegend>
        {!showModal && (
          <DatamapsIndia
            style={{
              '.HoverInfo': {
                position: 'absolute !important',
                top: '50% !important',
              },
            }}
            regionData={enhancedRegionData}
            hoverComponent={({ value }) => {
              return (
                <HoverInfo>
                  <div>{value.name}</div>
                  <div>Value: {value.value}</div>
                </HoverInfo>
              )
            }}
            mapLayout={{
              noDataColor: '#CCCCCC',
              borderColor: 'white',
              startColor: '#FFB84A',
              endColor: '#531011',
              hoverTitle: null,
              width: '100%',
              height: '100%',
              // Remove hover functionality
              hoverAnimation: false,
              // Remove the bottom legend
              legendTooltip: false,
              showLegend: false,
            }}
          />
        )}
      </MapContainer>

      {showModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalHeader>
              <ModalTitle fontSize="13px">
                Heat Map - No. of Colleges by State
              </ModalTitle>
              <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
            </ModalHeader>

            <div
              style={{
                position: 'relative',
                height: 'auto',
                border: '1px solid #C3C3C3',
              }}
            >
              <DatamapsIndia
                regionData={enhancedRegionData}
                hoverComponent={({ value }) => {
                  return (
                    <HoverInfo>
                      <div>{value.name}</div>
                      <div>Value: {value.value}</div>
                    </HoverInfo>
                  )
                }}
                mapLayout={{
                  noDataColor: '#CCCCCC',
                  borderColor: 'white',
                  startColor: '#FFB84A',
                  endColor: '#531011',
                  // color: [ "#FFB84A", "#F2891F", "#DE4424", "#9C0A0D", "#531011"],
                  width: '100%',
                  height: '100%',
                  hoverAnimation: true,
                  legendTooltip: true,
                  showLegend: false,
                }}
              />

              {/* Detailed Legend for Modal View */}
              <DetailedLegend>
                {/* Top states with specific colors based on count */}
                {Object.entries(regionData)
                  .sort((a, b) => b[1].value - a[1].value)
                  .map(([state, data], index) => (
                    <LegendRow key={index}>
                      <StateCount>
                        <ColorIndicator color={getColor(data.value)} />
                        {data.value}
                      </StateCount>
                      <div style={{ flexGrow: 1 }}>{state}</div>
                    </LegendRow>
                  ))}

                {/* Color scale moved to the bottom of the modal */}
              </DetailedLegend>
              <div
                style={{
                  padding: '10px 20px 20px',
                  // borderTop: '1px solid #eaeaea',
                  //  marginTop: '10px'
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    fontSize: '12px',
                  }}
                >
                  <div style={{ flex: '1', textAlign: 'center' }}>NA</div>
                  <div style={{ flex: '1', textAlign: 'center' }}>
                    1-5 Colleges
                  </div>
                  <div style={{ flex: '1', textAlign: 'center' }}>
                    6-10 Colleges
                  </div>
                  <div style={{ flex: '1', textAlign: 'center' }}>
                    11-15 Colleges
                  </div>
                  <div style={{ flex: '1', textAlign: 'center' }}>
                    16-20 Colleges
                  </div>
                  <div style={{ flex: '1', textAlign: 'center' }}>&gt;20</div>
                </div>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                    height: '20px',
                    marginBottom: '5px',
                  }}
                >
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#CCCCCC',
                      height: '100%',
                    }}
                  ></div>
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#FFB84A',
                      height: '100%',
                    }}
                  ></div>
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#F2891F',
                      height: '100%',
                    }}
                  ></div>
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#DE4424',
                      height: '100%',
                    }}
                  ></div>
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#9C0A0D',
                      height: '100%',
                    }}
                  ></div>
                  <div
                    style={{
                      flex: '1',
                      backgroundColor: '#531011',
                      height: '100%',
                    }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Color scale at the bottom of the modal */}
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  )
}

export { Map }
